import React, { useState, useEffect } from 'react'
import Button from 'components/button'
import Select from 'components/select'
import { useApplicationStore } from 'hooks/application'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import api from 'helpers/api'
import { getProjectTables } from 'helpers/utils'
import { IProject, ITable, ITableView } from 'types'
import { PROJECT } from 'app-constants'
import useProject from 'hooks/useProject'

interface ImportFromTableSelectionProps {
  onCsvReady: (csvData: string) => void
  onCancel: () => void
}

const ImportFromTableSelection: React.FC<ImportFromTableSelectionProps> = ({ onCsvReady, onCancel }) => {
  const { displayErrorMessage } = useApplicationStore()
  const { spreadsheetData } = useDataContext()
  const { project } = useProject()

  const [hubs, setHubs] = useState<IProject[]>([])
  const [tables, setTables] = useState<ITable[]>([])
  const [views, setViews] = useState<Array<{ publicId: string; name: string }>>([])

  const [selectedHubId, setSelectedHubId] = useState<string>('')
  const [selectedTableId, setSelectedTableId] = useState<string>('')
  const [selectedViewId, setSelectedViewId] = useState<string>('')

  const [loadingHubs, setLoadingHubs] = useState(false)
  const [loadingTables, setLoadingTables] = useState(false)
  const [loadingViews, setLoadingViews] = useState(false)
  const [loadingCsv, setLoadingCsv] = useState(false)

  // Fetch hubs on mount
  useEffect(() => {
    setLoadingHubs(true)
    api
      .getProjects()
      .then((res) => {
        setHubs(res.data)
        if (project.publicId && project.publicId !== '') {
          setSelectedHubId(project.publicId)
        }
      })
      .catch((e) => displayErrorMessage(e))
      .finally(() => setLoadingHubs(false))
  }, [])

  // Fetch tables when hub changes
  useEffect(() => {
    if (!selectedHubId) {
      setTables([])
      return
    }
    setLoadingTables(true)
    setSelectedTableId('')
    setSelectedViewId('')
    setViews([])
    getProjectTables(selectedHubId)
      .then((tables) => setTables(tables))
      .catch((e) => displayErrorMessage(e))
      .finally(() => setLoadingTables(false))
  }, [selectedHubId])

  // Fetch views when table changes
  useEffect(() => {
    if (!selectedTableId) {
      setViews([])
      return
    }
    setLoadingViews(true)
    setSelectedViewId('')
    api
      .getTableViews(selectedTableId, { ignoreColumns: true })
      .then((res) => {
        setViews(
          res.data.map((view: ITableView) => ({
            publicId: view.publicId,
            name: view.name
          }))
        )
      })
      .catch((e) => {
        displayErrorMessage(e)
        setViews([])
      })
      .finally(() => setLoadingViews(false))
  }, [selectedTableId])

  const handleContinue = async () => {
    if (!selectedViewId) return
    setLoadingCsv(true)
    try {
      const csvText = await api.getTableViewCSVAsText(selectedViewId)
      onCsvReady(csvText)
      setLoadingCsv(false)
    } catch (e) {
      displayErrorMessage(e)
      setLoadingCsv(false)
    }
  }

  return (
    <div>
      <div className="flex flex-col gap-[30px]">
        <div className="flex flex-col gap-2.5">
          <span className="font-bold mb-[3px]">
            Select Hub <strong className="text-red">*</strong>
          </span>
          <Select
            options={hubs
              .slice()
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((hub) => ({ value: hub.publicId, label: hub.name }))}
            onOptionClick={(value: string) => setSelectedHubId(value)}
            optionsSelected={selectedHubId ? [selectedHubId] : []}
            loading={loadingHubs}
          />
          <span className="text-muted text-sm">
            Select the hub containing your source table. Only {PROJECT}s you have admin access to will be shown.
          </span>
        </div>

        <div className="flex flex-col gap-2.5">
          <span className="font-bold">
            Select Table <strong className="text-red">*</strong>
          </span>
          <Select
            options={tables
              .slice()
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((table) => ({ value: table.publicId, label: table.name }))}
            onOptionClick={(value) => {
              setSelectedTableId(value)
              const table = tables.find((t) => t.publicId === value)
              if (table && table.defaultViewId !== spreadsheetData.viewDetails.publicId) {
                setSelectedViewId(table.defaultViewId)
              }
            }}
            optionsSelected={selectedTableId ? [selectedTableId] : []}
            loading={loadingTables}
            disabled={!selectedHubId}
          />
          <span className="text-muted text-sm">Select the table you want to import from.</span>
        </div>

        <div className="flex flex-col gap-2.5">
          <span className="font-bold">
            Select Table View <strong className="text-red">*</strong>
          </span>
          <Select
            options={views
              .filter((view) => view.publicId !== spreadsheetData.viewDetails.publicId)
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((view) => ({ value: view.publicId, label: view.name }))}
            onOptionClick={(value) => setSelectedViewId(value)}
            optionsSelected={selectedViewId ? [selectedViewId] : []}
            loading={loadingViews}
            disabled={!selectedTableId}
          />
          <span className="text-muted text-sm">Select the table view you want to import from.</span>
        </div>

        <div className="flex mt-2.5">
          <Button className="ml-auto mr-2.5 w-[150px]" internalType="danger" onClick={onCancel} disabled={loadingCsv}>
            Cancel
          </Button>
          <Button
            internalType="accept"
            className="w-[150px]"
            onClick={handleContinue}
            isLoading={loadingCsv}
            disabled={!selectedViewId}
          >
            Continue
          </Button>
        </div>
      </div>
    </div>
  )
}

export default ImportFromTableSelection
